import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Package, Search, EyeIcon, UserIcon, Download } from "lucide-react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { iPaginationResponse } from "core/kangarooTypes/iPaginationResponse";
import { iUser } from "core/kangarooTypes/iUser";
import { getUsers } from "core/Services/AdminServices";
import { downloadInventoryTemplate } from "core/Services/InventoryServices";
import { toast } from "react-toastify";
import FormInput from "shared/FormInput";
import { Link } from "react-router-dom";
import { getStatusColors } from "utils/statusColors";
import Button from "shared/Button";

const Inventory = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [merchants, setMerchants] = useState<iPaginationResponse<iUser>>();
  const [currentPage, setCurrentPage] = useState(1);

  const {
    register,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      search: "",
    },
  });

  const fetchData = async (search?: string, page: number = 1) => {
    try {
      setIsLoading(true);
      const response = await getUsers(
        {
          search: search,
          role: "merchant",
        },
        page
      );
      setMerchants(response);
    } catch (error) {
      toast.error(
        t("inventory.failedToLoadMerchants") || "Failed to load merchants."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData("");
  }, []);

  useEffect(() => {
    const subscription = watch((values) => {
      fetchData(values.search);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(watch("search"), page);
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadInventoryTemplate();
      toast.success(
        t("inventory.templateDownloaded") || "Template downloaded successfully!"
      );
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error(
        t("inventory.templateDownloadError") || "Error downloading template"
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex gap-4 flex-col p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-md shadow-sm p-4 sm:p-6 flex gap-4 flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Package size={32} className="text-primary" />
            <h1 className="text-2xl font-bold text-gray-800">
              {t("inventory.title")}
            </h1>
          </div>
          <Button
            className="flex items-center gap-2"
            onClick={handleDownloadTemplate}
            disabled={isDownloading}
            icon={<Download size={20} />}
          >
            <span>
              {isDownloading
                ? t("inventory.downloading")
                : t("inventory.downloadTemplate")}
            </span>
          </Button>
        </div>

        {/* Search Input */}
        <div className="w-full md:w-1/2 lg:w-1/3">
          <FormInput
            name="search"
            placeholder={t("inventory.searchPlaceholder")}
            register={register}
            errors={errors}
            leftIcon={<Search size={20} />}
          />
        </div>

        {/* Merchants Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase rounded-tl-lg">
                  {t("inventory.merchantName")}
                </th>
                <th className="text-left p-3 w-2/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.businessName")}
                </th>
                <th className="text-left p-3 w-2/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.email")}
                </th>
                <th className="text-left p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.phoneNumber")}
                </th>
                <th className="text-left p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase">
                  {t("inventory.status")}
                </th>
                <th className="text-right p-3 w-1/5 font-medium text-xs tracking-wider text-gray-500 uppercase rounded-tr-lg">
                  {t("inventory.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, index) => (
                    <tr key={index}>
                      <td className="py-4 px-3">
                        <div className="flex items-center">
                          <Skeleton
                            circle
                            width={32}
                            height={32}
                            className="mr-3"
                          />
                          <Skeleton width={120} />
                        </div>
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={180} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={150} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={100} />
                      </td>
                      <td className="py-4 px-3">
                        <Skeleton width={60} height={24} borderRadius={12} />
                      </td>
                      <td className="py-4 px-3 text-right">
                        <Skeleton width={20} height={20} />
                      </td>
                    </tr>
                  ))
              ) : merchants?.data && merchants.data.length > 0 ? (
                merchants.data.map((merchant) => (
                  <tr
                    key={merchant.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() =>
                      (window.location.hash = `#/inventory/${merchant.id}`)
                    }
                  >
                    <td className="py-4 px-3">
                      <div className="flex items-center">
                        <div className="bg-gray-200 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                          {merchant.id === 1 ? (
                            <UserIcon className="w-4 h-4" />
                          ) : (
                            <div className="text-xs font-medium">
                              {merchant.first_name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-sm">
                          {merchant.first_name} {merchant.last_name}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {merchant.business_name}
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {merchant.email}
                    </td>
                    <td className="py-4 px-3 text-gray-600 text-sm">
                      {merchant.phone_number}
                    </td>
                    <td className="py-4 px-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs
                          ${
                            getStatusColors(
                              merchant.active ? "active" : "deactivated"
                            ).bg
                          }
                          ${
                            getStatusColors(
                              merchant.active ? "active" : "deactivated"
                            ).text
                          }
                        `}
                      >
                        {merchant.active
                          ? t("inventory.active")
                          : t("inventory.deactivated")}
                      </span>
                    </td>
                    <td className="py-4 px-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Link
                          className="text-gray-500 hover:text-blue-600 transition-colors p-1 rounded hover:bg-gray-100"
                          to={`/inventory/${merchant.id}`}
                          title={t("inventory.viewInventory")}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    {t("inventory.noMerchantsFound")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && merchants?.meta && (
          <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6 space-y-4 sm:space-y-0">
            <div className="text-sm text-gray-500">
              {merchants.data && merchants.data.length > 0 ? (
                <>
                  {t("inventory.showing")} {merchants.meta.from}-
                  {merchants.meta.to} {t("inventory.of")} {merchants.meta.total}{" "}
                  {t("inventory.entries")}
                </>
              ) : (
                <>0 {t("inventory.entries")}</>
              )}
            </div>
            {merchants.data && merchants.data.length > 0 && (
              <div className="flex space-x-1">
                <button
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  {t("inventory.previous")}
                </button>
                <button className="px-3 py-1 border rounded text-sm bg-blue-600 text-white">
                  {currentPage}
                </button>
                <button
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  disabled={currentPage === merchants.meta.last_page}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {t("inventory.next")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;
